/** @jsx jsx */

import { Fragment } from 'react';

import {
  Dropdown,
  Field,
  FormLabel,
  LabeledRadio,
  Spacer,
  Text,
  TextInput,
} from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import SaveButtonGroup from 'app/developer-v3/components/SaveButtonGroup';
import { useForm, useIssue, useRouter } from 'app/developer-v3/hooks';
import {
  RESOLUTION_STATUSES_DISPLAY,
  WORK_STATUSES_DISPLAY,
} from '../constants';

import { RESOLUTION_STATUS_CHOICES, WORK_STATUS_CHOICES } from './constants';

const Styles = {
  block: css`
    padding: 15px;
  `,
};

type Values = {
  statusComment: string;
  workStatus: WORK_STATUSES_DISPLAY;
  resolutionStatus: RESOLUTION_STATUSES_DISPLAY;
};

type Props = {
  initialValues: Values;
  isLoading: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  saveText?: string;
};

function WorkStatusForm({
  initialValues,
  isLoading,
  isSaving,
  onCancel,
  onSubmit,
  saveText,
}: Props) {
  const { params } = useRouter();
  const { id: integrationId, issueId } = params;

  const { comment, toggleStatus, updateWorkStatus } = useIssue({
    integrationId: Number(integrationId),
    issueId: Number(issueId),
  });

  const form = useForm({
    enableReinitialize: true,
    initialValues,
    async onSubmit(valuesNext) {
      await comment.save({ body: valuesNext.statusComment });
      if (valuesNext.workStatus === WORK_STATUS_CHOICES.done.value) {
        toggleStatus(
          // this modal is only accessible if issue status is Open
          // so pass in Status as 1
          1,
          Object.keys(RESOLUTION_STATUSES_DISPLAY).indexOf(
            valuesNext.resolutionStatus
          ) + 1
        );
      } else {
        // otherwise update the work status as normal
        updateWorkStatus(
          Object.keys(WORK_STATUSES_DISPLAY).indexOf(valuesNext.workStatus) + 1
        );
      }
      onSubmit();
    },
    validate: valuesNext => {
      const { workStatus, resolutionStatus, statusComment } = valuesNext;
      const errorsNext: Partial<Record<keyof typeof valuesNext, string>> = {};

      if (statusComment) {
        if (statusComment.length < 20) {
          errorsNext.statusComment = `Comment must be at least ${20} characters.`;
        }
      } else {
        errorsNext.statusComment = 'Comment is required.';
      }

      if (!workStatus) {
        errorsNext.workStatus = 'Work Status is required.';
      }

      // if the work status is Done,
      // make sure there is also a Resolution chosen
      if (workStatus === WORK_STATUS_CHOICES.done.value && !resolutionStatus) {
        errorsNext.resolutionStatus =
          'Resolution is required for issue to be marked Done.';
      }

      return errorsNext;
    },
  });

  const isFormReadOnly = isLoading || isSaving;

  return (
    <Fragment>
      <Field
        error={form.isTouched.workStatus ? form.errors.workStatus : ''}
        helpTextLineClamp={2}
        isDisabled={isFormReadOnly}
        isRequired={true}
        label="Work Status"
        renderInput={inputProps => (
          <div css={Styles.block}>
            {Object.values(WORK_STATUS_CHOICES).map(choice => (
              <FormLabel key={choice.value}>
                <LabeledRadio
                  checked={form.values.workStatus === choice.value}
                  disabled={inputProps.isDisabled}
                  id={choice.value}
                  name="workStatus"
                  onChange={form.onChange}
                  tabIndex={0}
                  value={choice.value}
                >
                  {choice.label}
                </LabeledRadio>
                <Spacer height={5} />
                <Text>{choice.helpText}</Text>
              </FormLabel>
            ))}
          </div>
        )}
      />
      {form.values.workStatus === WORK_STATUS_CHOICES.done.value && (
        <Field
          error={
            form.isTouched.resolutionStatus ? form.errors.resolutionStatus : ''
          }
          helpTextLineClamp={2}
          isDisabled={isFormReadOnly}
          isRequired={form.values.workStatus === WORK_STATUS_CHOICES.done.value}
          label="Resolution Status"
          renderInput={inputProps => (
            <div css={Styles.block}>
              <Dropdown
                {...inputProps}
                aria-label="Resolution Status"
                items={RESOLUTION_STATUS_CHOICES}
                name="resolutionStatus"
                onBlur={form.onBlur}
                onChange={item =>
                  form.setFieldValue('resolutionStatus', item.value)
                }
              />
            </div>
          )}
        />
      )}
      <Field
        error={form.isTouched.statusComment ? form.errors.statusComment : ''}
        helpTextLineClamp={2}
        isDisabled={isFormReadOnly}
        isRequired={true}
        label="Status Comment"
        renderHelpText={() => (
          <Text color={isFormReadOnly ? 'GrayWarm5' : undefined} tag="p">
            Add details explaining this change to the work status of the issue.
          </Text>
        )}
        renderInput={inputProps => (
          <TextInput
            {...inputProps}
            aria-label="Status Comment"
            name="statusComment"
            onBlur={form.onBlur}
            onChange={form.onChange}
            value={form.values.statusComment}
          />
        )}
      />
      <SaveButtonGroup
        cancelText="Cancel"
        isDisabled={
          isFormReadOnly || !form.isValid || !form.isDirty || form.isSubmitting
        }
        isSaving={isSaving}
        margin="20px 0px 0px 0px"
        onCancel={onCancel}
        onSave={form.onSubmit}
        saveText={saveText}
      />
    </Fragment>
  );
}

export default WorkStatusForm;
